import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Table, Form, Button, Pagination, Alert, Badge, InputGroup, Card } from 'react-bootstrap';
import { Search, Eye, Edit, Trash2, Plus } from 'lucide-react';
import { portfolioProjectService } from '../services/portfolioProjectService';
import './Email.css';

const PortfolioProjectsList = forwardRef(({ onViewProject, onEditProject, onDeleteProject, onCreateProject }, ref) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(25);

  // Filters
  const [filters, setFilters] = useState({
    category: 'all',
    status: 'all',
    featured: 'all',
    search: ''
  });

  // Load projects
  const loadProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      const queryFilters = {
        ...filters,
        category: filters.category === 'all' ? undefined : filters.category,
        status: filters.status === 'all' ? undefined : filters.status,
        featured: filters.featured === 'all' ? undefined : filters.featured,
        page: currentPage,
        limit,
        sortBy: 'displayOrder',
        sortOrder: 'ASC'
      };
      
      // Remove undefined values
      Object.keys(queryFilters).forEach(key => 
        queryFilters[key] === undefined && delete queryFilters[key]
      );

      const response = await portfolioProjectService.getAllPortfolioProjects(queryFilters);

      if (response.success && response.data) {
        setProjects(response.data || []);
        setTotalPages(response.pagination?.totalPages || 1);
        setTotal(response.pagination?.total || 0);
      }
    } catch (err) {
      setError(err.message || 'Failed to load portfolio projects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProjects();
  }, [currentPage, filters, limit]);

  // Expose refresh method via ref
  useImperativeHandle(ref, () => ({
    refresh: loadProjects
  }));

  // Reset to page 1 when limit changes
  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setCurrentPage(1);
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const getStatusBadge = (status) => {
    const variants = {
      completed: 'success',
      'in-progress': 'warning',
      upcoming: 'info'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status?.replace('-', ' ').toUpperCase() || 'N/A'}</Badge>;
  };

  const getCategoryBadge = (category) => {
    const variants = {
      Commercial: 'primary',
      Industrial: 'info',
      Residential: 'success'
    };
    return <Badge bg={variants[category] || 'secondary'}>{category}</Badge>;
  };

  return (
    <div className="applications-list-container">
      {/* Filters */}
      <Card className="mb-4">
        <Card.Body>
          <div className="row g-3">
            <div className="col-md-3">
              <Form.Group>
                <Form.Label>Search</Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <Search size={16} />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search projects..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                  />
                </InputGroup>
              </Form.Group>
            </div>
            <div className="col-md-2">
              <Form.Group>
                <Form.Label>Category</Form.Label>
                <Form.Select
                  value={filters.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                >
                  <option value="all">All Categories</option>
                  <option value="Commercial">Commercial</option>
                  <option value="Industrial">Industrial</option>
                  <option value="Residential">Residential</option>
                </Form.Select>
              </Form.Group>
            </div>
            <div className="col-md-2">
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="completed">Completed</option>
                  <option value="in-progress">In Progress</option>
                  <option value="upcoming">Upcoming</option>
                </Form.Select>
              </Form.Group>
            </div>
            <div className="col-md-2">
              <Form.Group>
                <Form.Label>Featured</Form.Label>
                <Form.Select
                  value={filters.featured}
                  onChange={(e) => handleFilterChange('featured', e.target.value)}
                >
                  <option value="all">All</option>
                  <option value="true">Featured</option>
                  <option value="false">Not Featured</option>
                </Form.Select>
              </Form.Group>
            </div>
            <div className="col-md-2">
              <Form.Group>
                <Form.Label>Per Page</Form.Label>
                <Form.Select
                  value={limit}
                  onChange={(e) => handleLimitChange(parseInt(e.target.value))}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Form.Select>
              </Form.Group>
            </div>
            <div className="col-md-1 d-flex align-items-end">
              <Button variant="primary" onClick={() => onCreateProject && onCreateProject()}>
                <Plus size={16} /> Add
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Projects Table */}
      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : projects.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <p>No portfolio projects found.</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>Image</th>
                      <th>Name</th>
                      <th>Category</th>
                      <th>Location</th>
                      <th>Year</th>
                      <th>Status</th>
                      <th>Featured</th>
                      <th>Order</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.map((project) => (
                      <tr key={project.id}>
                        <td>
                          {project.image ? (
                            <img
                              src={project.image}
                              alt={project.imageAlt || project.name}
                              style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px' }}
                              onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/60x60?text=No+Image';
                              }}
                            />
                          ) : (
                            <div style={{ width: '60px', height: '60px', backgroundColor: '#f0f0f0', borderRadius: '4px' }} />
                          )}
                        </td>
                        <td>
                          <strong>{project.name}</strong>
                          {project.description && (
                            <div className="text-muted small mt-1" style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {project.description}
                            </div>
                          )}
                        </td>
                        <td>{getCategoryBadge(project.category)}</td>
                        <td>{project.location}</td>
                        <td>{project.year}</td>
                        <td>{getStatusBadge(project.status)}</td>
                        <td>
                          {project.featured ? (
                            <Badge bg="success">Yes</Badge>
                          ) : (
                            <Badge bg="secondary">No</Badge>
                          )}
                        </td>
                        <td>{project.displayOrder || 0}</td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => onViewProject && onViewProject(project.id)}
                              title="View"
                            >
                              <Eye size={16} />
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => onEditProject && onEditProject(project.id)}
                              title="Edit"
                            >
                              <Edit size={16} />
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => onDeleteProject && onDeleteProject(project.id)}
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div className="text-muted">
                    Showing {((currentPage - 1) * limit) + 1} to {Math.min(currentPage * limit, total)} of {total} projects
                  </div>
                  <Pagination>
                    <Pagination.First
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                    />
                    <Pagination.Prev
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    />
                    {[...Array(totalPages)].map((_, idx) => {
                      const page = idx + 1;
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 2 && page <= currentPage + 2)
                      ) {
                        return (
                          <Pagination.Item
                            key={page}
                            active={page === currentPage}
                            onClick={() => setCurrentPage(page)}
                          >
                            {page}
                          </Pagination.Item>
                        );
                      } else if (
                        page === currentPage - 3 ||
                        page === currentPage + 3
                      ) {
                        return <Pagination.Ellipsis key={page} />;
                      }
                      return null;
                    })}
                    <Pagination.Next
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                    />
                    <Pagination.Last
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
});

PortfolioProjectsList.displayName = 'PortfolioProjectsList';

export default PortfolioProjectsList;

